import React from "react";
import {
    Box, Button, Checkbox, Chip, FormControlLabel, IconButton,
    MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import AddRoundedIcon           from "@mui/icons-material/AddRounded";
import ArrowUpwardRoundedIcon   from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import BoltRoundedIcon          from "@mui/icons-material/BoltRounded";
import EmployeeAutocomplete from "../../../component/common/EmployeeAutocomplete";
import { STEP_RESOLVERS, VIEWER_RESOLVERS } from "./formAdminConstants";

/**
 * 연동 · 결재선 · 열람자.
 *
 * <h3>연동에 두 종류가 있다</h3>
 * <b>조회형</b>은 이 결재를 다른 화면이 읽어가기만 한다(자금수지 예정·사내일정). 껐다 켜도 흔적이 없다.
 * <b>생성형</b>은 승인 시 실제 행을 만든다(확정 원장·미수금·연차 차감). 잘못 켜서 승인이 나면
 * 꺼도 이미 생긴 건 남으므로, 화면에서 색과 문구로 갈라 놓는다.
 *
 * <h3>결재선은 사람이 아니라 규칙이다</h3>
 * «소속팀장»은 상신하는 사람마다 다르게 풀린다. 여기서 정하는 건 뼈대고, 실제 사람은
 * 상신 시점에 서버가 채운다 — 그래서 «지정 인물»일 때만 사람을 고른다.
 */
export default function RulesEditor({ rules, capabilities, onChange }) {
    const set = (patch) => onChange({ ...rules, ...patch });

    const caps = rules.capabilities || [];
    const line = rules.line || [];
    const viewers = rules.viewers || [];

    const toggleCap = (code) => set({
        capabilities: caps.includes(code) ? caps.filter((c) => c !== code) : [...caps, code],
    });

    const setStep = (i, patch) => set({ line: line.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
    const moveStep = (from, to) => {
        if (to < 0 || to >= line.length) return;
        const next = [...line];
        const [it] = next.splice(from, 1);
        next.splice(to, 0, it);
        set({ line: next });
    };

    return (
        <Box>
            {/* ── 연동 ── */}
            <Section title="연동" desc="승인했을 때 다른 시스템에 무엇을 할지">
                <Stack spacing={0.5}>
                    {capabilities.map((c) => {
                        const on = caps.includes(c.code);
                        return (
                            <Box key={c.code}
                                 onClick={() => toggleCap(c.code)}
                                 sx={{
                                     display: "flex", alignItems: "center", gap: 1,
                                     p: 1, borderRadius: 1.5, cursor: "pointer",
                                     border: "1px solid",
                                     borderColor: on ? (c.sideEffect ? "#fca5a5" : "#93c5fd") : "#e2e8f0",
                                     bgcolor:     on ? (c.sideEffect ? "#fef2f2" : "#eff6ff") : "#fff",
                                 }}>
                                <Checkbox size="small" checked={on} sx={{ p: 0 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <Typography sx={{ fontSize: 13, fontWeight: on ? 700 : 500 }}>
                                            {c.label}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={c.sideEffect ? "생성형" : "조회형"}
                                            icon={c.sideEffect ? <BoltRoundedIcon sx={{ fontSize: 12 }} /> : undefined}
                                            sx={{
                                                height: 17, fontSize: 10, fontWeight: 800,
                                                bgcolor: c.sideEffect ? "#fee2e2" : "#e0f2fe",
                                                color:   c.sideEffect ? "#b91c1c" : "#0369a1",
                                                "& .MuiChip-icon": { color: "#b91c1c", ml: 0.4 },
                                            }}
                                        />
                                    </Stack>
                                    {c.requiredSlots?.length > 0 && (
                                        <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.15 }}>
                                            필요한 칸: {c.requiredSlots.join(", ")}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
                {caps.some((code) => capabilities.find((c) => c.code === code)?.sideEffect) && (
                    <Typography sx={{ fontSize: 11, color: "#b91c1c", mt: 1, fontWeight: 600 }}>
                        생성형 연동이 켜져 있습니다. 승인되면 실제 데이터가 생기고, 나중에 꺼도 이미 생긴 건 남습니다.
                    </Typography>
                )}
            </Section>

            {/* ── 결재선 ── */}
            <Section title="결재선" desc="위에서부터 순서대로. 실제 사람은 상신할 때 채워진다">
                {line.map((s, i) => {
                    const meta = STEP_RESOLVERS.find((r) => r.value === s.resolver);
                    return (
                        <Stack key={i} direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                            <Box sx={{ width: 22, textAlign: "center", fontSize: 12,
                                       fontWeight: 800, color: "#94a3b8", flexShrink: 0 }}>
                                {i + 1}
                            </Box>
                            <TextField size="small" select value={s.resolver || "FIXED"}
                                       onChange={(e) => setStep(i, { resolver: e.target.value })}
                                       sx={{ width: 132, flexShrink: 0 }}>
                                {STEP_RESOLVERS.map((r) => (
                                    <MenuItem key={r.value} value={r.value} sx={{ fontSize: 13 }}>{r.label}</MenuItem>
                                ))}
                            </TextField>

                            <Box sx={{ flex: 1, minWidth: 140 }}>
                                {s.resolver === "FIXED" ? (
                                    <EmployeeAutocomplete
                                        value={s.employeeId ? { id: s.employeeId, name: s.employeeName } : null}
                                        onChange={(emp) => setStep(i, {
                                            employeeId: emp && typeof emp !== "string" ? emp.id : null,
                                            employeeName: emp && typeof emp !== "string" ? emp.name : "",
                                        })}
                                        placeholder="결재자 선택"
                                        variant="outlined"
                                        disableUnderline={false}
                                    />
                                ) : (
                                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{meta?.hint}</Typography>
                                )}
                            </Box>

                            <FormControlLabel
                                sx={{ m: 0, flexShrink: 0 }}
                                control={<Checkbox size="small" checked={!!s.editable}
                                                   onChange={(e) => setStep(i, { editable: e.target.checked })} />}
                                label={<Typography sx={{ fontSize: 11, color: "#64748b" }}>상신자가 변경 가능</Typography>}
                            />

                            <Stack sx={{ flexShrink: 0 }}>
                                <IconButton size="small" disabled={i === 0}
                                            onClick={() => moveStep(i, i - 1)} sx={{ p: 0.15 }}>
                                    <ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <IconButton size="small" disabled={i === line.length - 1}
                                            onClick={() => moveStep(i, i + 1)} sx={{ p: 0.15 }}>
                                    <ArrowDownwardRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Stack>
                            <IconButton size="small" sx={{ color: "#ef4444", flexShrink: 0 }}
                                        onClick={() => set({ line: line.filter((_, j) => j !== i) })}>
                                <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Stack>
                    );
                })}

                <Button size="small" startIcon={<AddRoundedIcon />}
                        onClick={() => set({ line: [...line, { resolver: "FIXED", editable: false }] })}
                        sx={{ fontSize: 12, fontWeight: 700 }}>
                    단계 추가
                </Button>

                <Typography sx={{ fontSize: 11, color: "#64748b", mt: 1, lineHeight: 1.6 }}>
                    스킵 규칙 — 팀장 미지정·기안자 본인·앞 단계와 같은 사람은 자동으로 건너뜁니다.
                    다만 <b>마지막 단계의 기안자 본인은 남습니다</b> — 최종 결재자는 자기 문서도 스스로 확정합니다.
                    그러고도 결재자가 0명이면 <b>대표이사</b>에게 올라갑니다.
                </Typography>
            </Section>

            {/* ── 열람자 ── */}
            <Section title="열람자(공람)" desc="결재 권한 없이 보기·댓글만 가능한 사람">
                {viewers.map((v, i) => (
                    <Stack key={i} direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                        <TextField size="small" select value={v.resolver || "FIXED"}
                                   onChange={(e) => set({ viewers: viewers.map((x, j) =>
                                       (j === i ? { ...x, resolver: e.target.value } : x)) })}
                                   sx={{ width: 132, flexShrink: 0 }}>
                            {VIEWER_RESOLVERS.map((r) => (
                                <MenuItem key={r.value} value={r.value} sx={{ fontSize: 13 }}>{r.label}</MenuItem>
                            ))}
                        </TextField>
                        <Box sx={{ flex: 1, minWidth: 140 }}>
                            {v.resolver === "FIXED" ? (
                                <EmployeeAutocomplete
                                    value={v.employeeId ? { id: v.employeeId, name: v.employeeName } : null}
                                    onChange={(emp) => set({ viewers: viewers.map((x, j) => (j === i ? {
                                        ...x,
                                        employeeId: emp && typeof emp !== "string" ? emp.id : null,
                                        employeeName: emp && typeof emp !== "string" ? emp.name : "",
                                    } : x)) })}
                                    placeholder="열람자 선택"
                                    variant="outlined"
                                    disableUnderline={false}
                                />
                            ) : (
                                <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                                    {VIEWER_RESOLVERS.find((r) => r.value === v.resolver)?.hint}
                                </Typography>
                            )}
                        </Box>
                        <IconButton size="small" sx={{ color: "#ef4444", flexShrink: 0 }}
                                    onClick={() => set({ viewers: viewers.filter((_, j) => j !== i) })}>
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Stack>
                ))}
                <Button size="small" startIcon={<AddRoundedIcon />}
                        onClick={() => set({ viewers: [...viewers, { resolver: "FIXED" }] })}
                        sx={{ fontSize: 12, fontWeight: 700 }}>
                    열람자 추가
                </Button>
            </Section>
        </Box>
    );
}

function Section({ title, desc, children }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{title}</Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mb: 1.25 }}>{desc}</Typography>
            {children}
        </Box>
    );
}
